import React, { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '../../../core/design-system/components/Text';
import { Icon } from '../../../core/design-system/components/Icon';
import { formatActivityDate } from '../../../shared/utils/date/DateFormatters';
import { useActivityNavigation } from '../../hooks/useActivityNavigation';
import { getDisplayLocation } from '../../utils/location/getDisplayLocation';
import ClickableAvatar from '../ClickableAvatar';
import './styles.css';
import { getActivityTitle } from '../../utils/activity/getActivityTitle';

const FeedCard = ({ post, onActivityClick }) => {
  const { t, i18n } = useTranslation('common');
  const { goToActivityDetails } = useActivityNavigation();

  // Récupérer les infos de catégorie depuis l'activité
  const categoryInfo = useMemo(() => {
    if (!post.activity?.type) return null;
    const type = post.activity.type;
    if (typeof type === 'object' && type.labels) {
      return {
        id: type.id,
        label: type.labels[i18n.language] || type.labels.en,
        icon: type.icon
      };
    }
    return null;
  }, [post.activity?.type, i18n.language]);

  // Générer un titre selon la catégorie
  const activityTitle = useMemo(() => {
    return getActivityTitle({ activity: post.activity, t, i18n });
  }, [post.activity, t, i18n]);

  // Toujours une string pour interpolation ou affichage
  const activityTitleString = typeof activityTitle === 'object'
    ? activityTitle[i18n.language] || activityTitle.en
    : activityTitle;

  // Formater la date et l'heure
  // i18n.language nécessaire pour re-render au changement de langue
  const formattedDate = useMemo(() => {
    if (!post.activity?.date) return t('activity.date_tbd');
    return formatActivityDate(post.activity.date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.activity?.date, t, i18n.language]);

  // Extraire la localisation à afficher
  // NOTE: Privilégie le nom du POI, puis la ville, puis l'adresse
  const locationInfo = useMemo(() => {
    return getDisplayLocation(post.activity?.location);
  }, [post.activity?.location]);
  
  // Distance séparée de la ville
  const distanceInfo = useMemo(() => {
    if (!post.activity?.distance) return null;
    return `${Math.round(post.activity.distance)} km`;
  }, [post.activity?.distance]);

  // Formater le nombre de participants
  const participantsInfo = useMemo(() => {
    const current = post.activity?.currentParticipants || 0;
    const max = post.activity?.maxParticipants;
    
    if (max) {
      return t('activity.participants_max', { current, max });
    }
    
    return t('activity.participants', { count: current });
  }, [post.activity?.currentParticipants, post.activity?.maxParticipants, t]);

  // Handler pour naviguer vers les détails
  const handleCardClick = () => {
    if (post?.id) {
      // Si callback fourni (mode modal), l'utiliser
      if (onActivityClick) {
        onActivityClick(post.id);
      } else {
        goToActivityDetails(post.id);
      }
    }
  };

  useEffect(() => {
    // DEBUG: log l'activité reçue
    // eslint-disable-next-line no-console
    console.log('FeedCard activity:', post.activity);
  }, [post.activity]);

  return (
    <div className="feed-card" onClick={handleCardClick}>
      {categoryInfo && (
        <div className={`activity-badge ${categoryInfo.id}`}>
          <span className="activity-badge-icon">{categoryInfo.icon}</span>
          <span className="activity-badge-text">{categoryInfo.label}</span>
        </div>
      )}
      
      <div className="feed-card-content">
        <div className="feed-card-header">
          <Text variant="h3" className="feed-card-title">{activityTitleString}</Text>
          <Icon name="chevron-right" size={20} className="feed-card-chevron" />
        </div>
        
        <div className="feed-card-details">
          <div className="feed-card-info">
            <div className="feed-card-detail">
              <Icon name="calendar" size={14} />
              <Text variant="body2">{formattedDate}</Text>
            </div>
            
            <div className="feed-card-location-container">
              {locationInfo && (
                <div className="feed-card-detail">
                  <Icon name="map-pin" size={14} />
                  <Text variant="body2">{locationInfo}</Text>
                </div>
              )}
              
              {distanceInfo && (
                <div className="feed-card-distance">
                  <Text variant="caption">{distanceInfo}</Text>
                </div>
              )}
            </div>
          </div>
          
          {/* Participants sur une ligne séparée */}
          <div className="feed-card-participants-row">
            <div className="feed-card-detail participants">
              <Icon name="users" size={14} />
              <Text variant="body2">{participantsInfo}</Text>
            </div>
          </div>
        </div>
        
        <div className="feed-card-author-container">
          <div className="feed-card-author">
            <ClickableAvatar 
              user={post.author}
              size="xs"
              className="feed-card-avatar"
            />
            <Text variant="caption" className="feed-card-author-name">{post.author?.name}</Text>
          </div>
          <div className="feed-card-author-label">
            <Text variant="caption">{t('activity.proposed_by')}</Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedCard;